import { defineStore } from 'pinia';
import { shouldThrottle } from '@/util/rateLimit';
import posthog from '@/composables/usePostHog.js';
import apiClient from '@/util/apiClient.ts';

export interface GroupSettingDTO {
	settingNumber: number | null;
	groupNumber: number | null;
	groupOwner: number | null;
	groupType: string | null;
	sport: string | null;
	plan: string | null;
	travelEnforcement: string | null;
	defaultRank: number | null;
}

export interface SaveGroupSettingPayload {
	groupNumber: number;
	groupOwnerNumber: number | null;
	groupType: string | null;
	groupStatus: string | null;
	sport: string | null;
	groupPlan: string | null;
	travelEnforcementMode: string | null;
	defaultRank: number | null;
}

export interface UpdateGroupSettingPayload {
	groupOwner?: { userNumber: number } | null;
	groupType?: string | null;
	groupStatus?: string | null;
	sport?: string | null;
	groupPlan?: string | null;
	travelEnforcementMode?: string | null;
	defaultRank?: number | null;
}

interface GroupSettingState {
	groupSetting: GroupSettingDTO | null;
	loading: boolean;
	error: string | null;
}

export const useGroupSettingStore = defineStore('groupSettingStore', {
	state: (): GroupSettingState => ({
		groupSetting: null,
		loading: false,
		error: null
	}),

	actions: {
		async fetchGroupSetting(groupNumber: number): Promise<GroupSettingDTO | null> {
			if (shouldThrottle('fetchGroupSetting', 100)) {
				posthog.capture('throttled_action', { action: 'fetchGroupSetting' });
				return this.groupSetting;
			}

			this.loading = true;
			this.error = null;

			try {
				const response = await apiClient.get<GroupSettingDTO | GroupSettingDTO[]>(`/group/settings/group/${groupNumber}`);

				const data = response.data;
				this.groupSetting = Array.isArray(data) ? (data[0] ?? null) : data;

				return this.groupSetting;
			} catch (error) {
				this.error = error instanceof Error ? error.message : 'Failed to fetch group setting';

				posthog.capture('group_setting_fetch_failed', {
					groupNumber,
					error: this.error
				});

				this.groupSetting = null;
				return null;
			} finally {
				this.loading = false;
			}
		},

		async saveGroupSetting(payload: SaveGroupSettingPayload): Promise<GroupSettingDTO | null> {
			if (shouldThrottle('saveGroupSetting', 100)) {
				posthog.capture('throttled_action', { action: 'saveGroupSetting' });
				return this.groupSetting;
			}

			this.loading = true;
			this.error = null;

			try {
				const response = await apiClient.post<GroupSettingDTO>('/group/settings/save', payload);

				this.groupSetting = response.data;
				return this.groupSetting;
			} catch (error) {
				this.error = error instanceof Error ? error.message : 'Failed to save group setting';

				posthog.capture('group_setting_save_failed', {
					groupNumber: payload.groupNumber,
					error: this.error
				});

				return null;
			} finally {
				this.loading = false;
			}
		},

		async updateGroupSetting(settingNumber: number, payload: UpdateGroupSettingPayload): Promise<GroupSettingDTO | null> {
			if (shouldThrottle('updateGroupSetting', 100)) {
				posthog.capture('throttled_action', { action: 'updateGroupSetting' });
				return this.groupSetting;
			}

			this.loading = true;
			this.error = null;

			try {
				const response = await apiClient.patch<GroupSettingDTO>(`/group/settings/edit/${settingNumber}`, payload);

				this.groupSetting = response.data;
				return this.groupSetting;
			} catch (error) {
				this.error = error instanceof Error ? error.message : 'Failed to update group setting';

				posthog.capture('group_setting_update_failed', {
					settingNumber,
					error: this.error
				});

				return null;
			} finally {
				this.loading = false;
			}
		},

		clearGroupSetting(): void {
			this.groupSetting = null;
			this.error = null;
			this.loading = false;
		}
	}
});
